// Shared constants for Festive module pages (§3.65–3.89, §2.26, §3.68)
// Eliminates duplication of ROLE_LEVELS, status maps, and date formatting.

import { ROLE_LEVELS } from '@/types/user';
import type { FestiveOrderStatus, FestiveEventStatus, CapacityStatus, AlertUrgency, AlertCategory } from '@/services/festive.service';

export { ROLE_LEVELS };

// ─── Badge variant type (matches @bbpos/ui Badge) ───────────────────────────

export type BadgeVariant = 'success' | 'warning' | 'draft' | 'danger' | 'active' | 'pending' | 'info';

// ─── Festive Order Status ───────────────────────────────────────────────────

export const ORDER_STATUS_BADGE: Record<FestiveOrderStatus, { label: string; variant: BadgeVariant }> = {
  BOOKED: { label: 'Booked', variant: 'info' },
  CONFIRMED: { label: 'Confirmed', variant: 'success' },
  IN_PRODUCTION: { label: 'In Production', variant: 'warning' },
  READY: { label: 'Ready', variant: 'active' },
  DELIVERED: { label: 'Delivered', variant: 'success' },
  CANCELLED: { label: 'Cancelled', variant: 'danger' },
};

// Statuses persisted by the DB are CONFIRMED → READY → DELIVERED (+ CANCELLED).
export const ORDER_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'READY', label: 'Ready' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// ─── Festive Event Status ───────────────────────────────────────────────────

export const EVENT_STATUS_BADGE: Record<FestiveEventStatus, { label: string; variant: BadgeVariant }> = {
  UPCOMING: { label: 'Upcoming', variant: 'warning' },
  ACTIVE: { label: 'Active', variant: 'success' },
  COMPLETED: { label: 'Completed', variant: 'draft' },
};

export const EVENT_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'UPCOMING', label: 'Upcoming' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
];

// ─── Capacity Status ────────────────────────────────────────────────────────

export const CAPACITY_BADGE: Record<CapacityStatus, { label: string; variant: BadgeVariant }> = {
  OPEN: { label: 'Open', variant: 'success' },
  FILLING: { label: 'Filling', variant: 'warning' },
  FULL: { label: 'Full', variant: 'danger' },
  AUTO_STOPPED: { label: 'Auto-Stopped', variant: 'danger' },
};

// ─── Alert Urgency ──────────────────────────────────────────────────────────

export const URGENCY_STYLE: Record<AlertUrgency, {
  variant: BadgeVariant;
  bg: string;
  icon: string;
}> = {
  URGENT: {
    variant: 'danger',
    bg: 'bg-rose-50/80 dark:bg-rose-900/20',
    icon: 'text-rose-600 dark:text-rose-400',
  },
  UPCOMING: {
    variant: 'warning',
    bg: 'bg-amber-50/80 dark:bg-amber-900/20',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  INFORMATIONAL: {
    variant: 'info',
    bg: 'bg-sky-50/80 dark:bg-sky-900/20',
    icon: 'text-sky-600 dark:text-sky-400',
  },
};

// ─── Cancellation ───────────────────────────────────────────────────────────

export const CANCELLATION_REASONS = [
  { value: 'CUSTOMER_REQUEST', label: 'Customer Request' },
  { value: 'EVENT_CANCELLED', label: 'Event Cancelled' },
  { value: 'CAPACITY_ISSUE', label: 'Capacity Issue' },
  { value: 'INGREDIENT_SHORTAGE', label: 'Ingredient Shortage' },
  { value: 'OTHER', label: 'Other' },
];

export const REFUND_METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'ORIGINAL_PAYMENT', label: 'Original Payment Reversal' },
  { value: 'STORE_CREDIT', label: 'Store Credit' },
];

// ─── Menu Categories ────────────────────────────────────────────────────────

export const MENU_CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'SWEETS', label: 'Sweets' },
  { key: 'FAMILY_PACKS', label: 'Family Packs' },
  { key: 'PARTY_TRAYS', label: 'Party Trays' },
  { key: 'CATERING', label: 'Catering' },
  { key: 'COMBOS', label: 'Combos' },
];

export const MENU_CATEGORY_OPTIONS = MENU_CATEGORIES.filter((c) => c.key !== 'all').map((c) => ({
  value: c.key,
  label: c.label,
}));

// ─── Date Formatting ────────────────────────────────────────────────────────

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} — ${formatDate(end)}`;
}

// ─── Pagination ─────────────────────────────────────────────────────────────

export const PAGE_SIZE = 20;
